import { animated } from "@react-spring/web";
import { useAtom } from "jotai";
import type { FunctionComponent, ReactNode } from "react";
import { useCallback, useEffect } from "react";

import { BaseLink } from "~/components/common/base-link";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useDocumentBodyScrollLock } from "~/lib/hooks";
import { page as postiesPage } from "~/pages/posties";

import { useHamburgerMenuAnimation } from "./animations";
import { hamburgerMenuAtom } from "./atoms";
import {
  Background,
  Container,
  ContentWrapper,
  Spacer,
  StyledIconClose,
  StyledIconMenu,
  StyledLogo,
  Wrapper,
} from "./styles";

type Props = {
  children?: ReactNode;
  className?: string;
};

export const HamburgerMenu: FunctionComponent<Props> = ({ children, className }) => {
  const [state, setState] = useAtom(hamburgerMenuAtom);
  const AnimatedWrapper = animated(Wrapper);
  const AnimatedBackground = animated(Background);
  const AnimatedContentWrapper = animated(ContentWrapper);
  const { backgroundStyles, contentWrapperStyles, wrapperStyles } = useHamburgerMenuAnimation();
  const { lockDocumentBodyScroll } = useDocumentBodyScrollLock();

  const handleClick = useCallback(
    (isOpen: boolean) => {
      setState(current => ({ ...current, isOpen }));
      if (isOpen) {
        sendGATracking({
          action: "Menu/Open",
          category: "Menu",
          label: "Menu/Open",
        });
      } else {
        sendGATracking({
          action: "Menu/Close",
          category: "Menu",
          label: "Menu/Close",
        });
      }
    },
    [setState],
  );

  useEffect(() => {
    lockDocumentBodyScroll(state.isOpen);
  }, [lockDocumentBodyScroll, state.isOpen]);

  return (
    <Container className={className}>
      <AnimatedWrapper style={wrapperStyles}>
        <AnimatedBackground style={backgroundStyles} />
        <AnimatedContentWrapper style={contentWrapperStyles}>
          <BaseLink {...postiesPage.route({})}>
            <StyledLogo />
          </BaseLink>
          <Spacer />
          {children}
          <Spacer />
        </AnimatedContentWrapper>
      </AnimatedWrapper>
      {state.isOpen ? (
        <StyledIconClose onClick={() => handleClick(false)} />
      ) : (
        <StyledIconMenu onClick={() => handleClick(true)} />
      )}
    </Container>
  );
};

HamburgerMenu.displayName = "HamburgerMenu";
